import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Toaster } from '@/components/ui/sonner';
import HelpButton from '@/components/HelpButton';
import ErrorBoundary from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import SettingsPage from './pages/SettingsPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateBuilder from './pages/TemplateBuilder';
import TemplateEditor from './pages/TemplateEditor';
import TemplateRun from './pages/TemplateRun';
import RunsPage from './pages/RunsPage';
import MarketplacePage from './pages/MarketplacePage';
import GlobalFields from './pages/GlobalFields';
import CategoriesPage from './pages/CategoriesPage';
import PdfTemplateBuilder from './pages/PdfTemplateBuilder';
import FormsPage from './pages/FormsPage';
import SelectTemplatePage from './pages/SelectTemplatePage';
// CRM Pages
import FirmProfilePage from './pages/FirmProfilePage';
import ClientsPage from './pages/ClientsPage';
import ClientFormPage from './pages/ClientFormPage';
import OpposingCounselPage from './pages/OpposingCounselPage';
import CasesPage from './pages/CasesPage';
import CourtsPage from './pages/CourtsPage';
import CaseFormPage from './pages/CaseFormPage';
import CaseDetailPage from './pages/CaseDetailPage';
// Admin Pages
import AdminLayout from './layouts/AdminLayout';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminTicketsPage from './pages/admin/AdminTicketsPage';
import AdminErrorsPage from './pages/admin/AdminErrorsPage';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminFormsPage from './pages/admin/AdminFormsPage';
import AdminPdfToolsPage from './pages/admin/AdminPdfToolsPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TicketDetailPage from './pages/TicketDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import ContactPage from './pages/ContactPage';
import SmartDocumentParsingPage from './pages/SmartDocumentParsingPage';
import DragDropFormBuilderPage from './pages/DragDropFormBuilderPage';
import InstantMailMergePage from './pages/InstantMailMergePage';
import TemplateManagementPage from './pages/TemplateManagementPage';
import HowItWorksPage from './pages/HowItWorksPage';
import './App.css';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/templates/builder" element={<ProtectedRoute><TemplateBuilder /></ProtectedRoute>} />
      <Route path="/templates/pdf-builder" element={<ProtectedRoute><PdfTemplateBuilder /></ProtectedRoute>} />
      <Route path="/templates/:templateId/edit" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
      <Route path="/templates/:templateId/run" element={<ProtectedRoute><TemplateRun /></ProtectedRoute>} />
      <Route path="/runs" element={<ProtectedRoute><RunsPage /></ProtectedRoute>} />
      <Route path="/global-fields" element={<ProtectedRoute><GlobalFields /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      {/* CRM Routes */}
      <Route path="/firm" element={<ProtectedRoute><FirmProfilePage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/clients/new" element={<ProtectedRoute><ClientFormPage /></ProtectedRoute>} />
      <Route path="/clients/:clientId/edit" element={<ProtectedRoute><ClientFormPage /></ProtectedRoute>} />
      <Route path="/courts" element={<ProtectedRoute><CourtsPage /></ProtectedRoute>} />

      <Route path="/opposing-counsel" element={<ProtectedRoute><OpposingCounselPage /></ProtectedRoute>} />
      <Route path="/cases" element={<ProtectedRoute><CasesPage /></ProtectedRoute>} />
      <Route path="/cases/new" element={<ProtectedRoute><CaseFormPage /></ProtectedRoute>} />
      <Route path="/cases/edit/:caseId" element={<ProtectedRoute><CaseFormPage /></ProtectedRoute>} />
      <Route path="/cases/:caseId" element={<ProtectedRoute><CaseDetailPage /></ProtectedRoute>} />
      <Route path="/forms" element={<ProtectedRoute><FormsPage /></ProtectedRoute>} />
      <Route path="/select-template" element={<ProtectedRoute><SelectTemplatePage /></ProtectedRoute>} />
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="tickets" element={<AdminTicketsPage />} />
        <Route path="errors" element={<AdminErrorsPage />} />
        <Route path="plans" element={<AdminPlansPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="forms" element={<AdminFormsPage />} />
        <Route path="pdf-tools" element={<AdminPdfToolsPage />} />
      </Route>
      <Route path="/super-admin" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/tickets/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/tickets/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/help-tickets/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/features/smart-document-parsing" element={<SmartDocumentParsingPage />} />
      <Route path="/features/drag-drop-form-builder" element={<DragDropFormBuilderPage />} />
      <Route path="/features/instant-mail-merge" element={<InstantMailMergePage />} />
      <Route path="/features/template-management" element={<TemplateManagementPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
    </Routes>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Not authenticated');
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      }
    };

    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setCurrentUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          }
        } catch (error) {
          console.error('Error fetching user:', error);
        }
      }
    };
    fetchUser();
    
    // Global error handler for uncaught errors
    const handleError = (event) => {
      logErrorToBackend({
        error_type: 'frontend',
        message: event.message || event.error?.message || 'Unknown error',
        stack_trace: event.error?.stack || event.filename,
        url: window.location.href,
        browser: navigator.userAgent
      });
    };

    // Global handler for unhandled promise rejections
    const handleRejection = (event) => {
      logErrorToBackend({
        error_type: 'frontend',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack_trace: event.reason?.stack || String(event.reason),
        url: window.location.href,
        browser: navigator.userAgent
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const logErrorToBackend = async (errorData) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await fetch(`${API}/error-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...errorData,
          user_id: user.user_id || null,
          user_email: user.email || null
        })
      });
    } catch (err) {
      console.error('Failed to log error to backend:', err);
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <AppRouter />
          <Toaster position="top-right" richColors />
          {currentUser && <HelpButton user={currentUser} />}
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };